import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { SUBSCRIPTION_LIMITS } from '@/types/subscription';
import { User, Crown, Calendar, CheckCircle2, AlertCircle, Sparkles, Gift, Settings, X, Trash } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ProfileModal({ isOpen, onClose }: ProfileModalProps) {
  const { user, signOut } = useAuth();
  const { subscription, activeProductCount, showUpgradeModal, fetchSubscription } = useSubscription();
  const [name, setName] = useState(user?.user_metadata?.full_name || '');
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      fetchProfile();
    }
  }, [user]);

  const fetchProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user?.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching profile:', error);
        return;
      }
      
      setName(data.full_name || user?.user_metadata?.full_name || '');
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const handleUpdateProfile = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: name,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) throw error;
      
      // Aktualisiere auch die User Metadata
      const { error: updateError } = await supabase.auth.updateUser({
        data: { full_name: name }
      });

      if (updateError) throw updateError;
      
      toast.success('✅ Profil erfolgreich gespeichert!', {
        description: 'Deine Änderungen wurden übernommen.',
        duration: 4000,
      });
      
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('❌ Fehler beim Speichern', {
        description: 'Das Profil konnte nicht aktualisiert werden. Bitte versuche es erneut.',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartTrial = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      // Zuerst die aktuelle Subscription abrufen
      const { data: currentSub, error: fetchError } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (fetchError) {
        console.error('Error fetching subscription:', fetchError);
        throw new Error('Fehler beim Abrufen der aktuellen Subscription');
      }

      if (!currentSub) {
        throw new Error('Keine Subscription gefunden');
      }

      // Überprüfen, ob Trial verfügbar ist
      if (currentSub.trial_used) {
        throw new Error('Testphase bereits verwendet');
      }

      // Subscription auf Trial aktualisieren
      const { error } = await supabase
        .from('subscriptions')
        .update({
          tier: 'trial',
          status: 'active',
          trial_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          trial_used: true,
          current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          cancel_at_period_end: true,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id);

      if (error) throw error;
      
      await fetchSubscription();
      toast.success('30-Tage Testphase erfolgreich aktiviert!');
    } catch (error: any) {
      console.error('Error starting trial:', error);
      toast.error(error.message || 'Fehler beim Aktivieren der Testphase');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;
    if (deleteConfirmText !== "LÖSCHEN") {
      toast.error('Bitte gib den Bestätigungstext ein', {
        duration: 3000,
      });
      return;
    }
    
    setIsLoading(true);
    try {
      // 1. Lösche alle Produkte (waitlist)
      const { error: waitlistError } = await supabase
        .from('waitlist')
        .delete()
        .eq('user_id', user.id);
      
      if (waitlistError) throw waitlistError;
      
      // 2. Lösche die Preishistorie
      const { error: priceHistoryError } = await supabase
        .from('price_history')
        .delete()
        .eq('user_id', user.id);
      
      if (priceHistoryError) throw priceHistoryError;
      
      // 3. Lösche das Abonnement
      const { error: subscriptionError } = await supabase
        .from('subscriptions')
        .delete()
        .eq('user_id', user.id);
      
      if (subscriptionError) throw subscriptionError;
      
      // 4. Lösche das Profil
      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', user.id);
      
      if (profileError) throw profileError;
      
      // 5. Lösche den Benutzer (Auth)
      const { error: authError } = await supabase.auth.admin.deleteUser(user.id);
      
      if (authError) throw authError;
      
      // Erfolgreich gelöscht, abmelden und zur Startseite navigieren
      await signOut();
      onClose();
      
      toast.success('Konto erfolgreich gelöscht', {
        description: 'Alle deine Daten wurden gelöscht. Wir hoffen, dich bald wieder bei KaufPause begrüßen zu dürfen!',
        duration: 5000,
      });
      
      navigate('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error('Fehler beim Löschen des Kontos', {
        description: 'Bitte versuche es später erneut oder kontaktiere den Support.',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
      setShowDeleteDialog(false);
    }
  };

  const getCurrentPlan = () => {
    if (!subscription) return SUBSCRIPTION_LIMITS.free;
    return SUBSCRIPTION_LIMITS[subscription.tier];
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const isTrialAvailable = !subscription?.trial_used && subscription?.tier === 'free';

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-w-[95vw] max-h-[90vh] overflow-y-auto mx-2 sm:mx-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Profil & Abonnement
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Profil-Bereich */}
          <Card>
            <CardHeader>
              <CardTitle>Persönliche Daten</CardTitle>
              <CardDescription>Verwalte deine Profilinformationen</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={user?.user_metadata?.full_name || "Dein Name"}
                  autoFocus={false}
                  autoComplete="off"
                  onClick={(e) => e.currentTarget.blur()}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail</Label>
                <Input
                  id="email"
                  value={user?.email || ''}
                  disabled
                  type="email"
                  className="bg-gray-50 text-gray-500"
                />
                <p className="text-xs text-gray-500">
                  Die E-Mail-Adresse kann aus Sicherheitsgründen nicht geändert werden.
                </p>
              </div>
              <Button 
                onClick={handleUpdateProfile} 
                disabled={isLoading}
                className="w-full"
              >
                Profil aktualisieren
              </Button>
            </CardContent>
              <CardFooter className="flex justify-end">
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="text-gray-500 hover:text-gray-700 text-xs flex items-center gap-1"
                  onClick={() => setShowDeleteDialog(true)}
                >
                  <Settings className="h-3 w-3" />
                  Einstellungen verwalten
                </Button>
              </CardFooter>
          </Card>

          {/* Abo-Bereich */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-yellow-500" />
                Dein Abonnement
              </CardTitle>
              <CardDescription>Details zu deinem aktuellen Plan</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">{getCurrentPlan().name}</h3>
                  <p className="text-sm text-gray-500">{getCurrentPlan().description}</p>
                </div>
                <Badge variant={subscription?.tier === 'free' ? 'secondary' : 'default'}>
                  {getCurrentPlan().price > 0 ? `${getCurrentPlan().price}€/${subscription?.tier === 'yearly' ? 'Jahr' : 'Monat'}` : 'Kostenlos'}
                </Badge>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className={`w-4 h-4 ${activeProductCount >= getCurrentPlan().maxActiveProducts ? 'text-red-500' : 'text-green-500'}`} />
                  <span>
                    {activeProductCount} von {getCurrentPlan().maxActiveProducts === Infinity ? '∞' : getCurrentPlan().maxActiveProducts} Produkten in Verwendung
                  </span>
                </div>

                {subscription?.trial_end && (
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-blue-500" />
                    <span>Testphase endet am {formatDate(subscription.trial_end)}</span>
                  </div>
                )}

                {subscription?.status === 'active' && subscription.current_period_end && subscription.tier === 'yearly' && (
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-blue-500" />
                    <span>Premium endet am {formatDate(subscription.premium_end_date || subscription.current_period_end)}</span>
                  </div>
                )}

                {subscription?.cancel_at_period_end && (
                  <div className="flex items-center gap-2 text-sm text-orange-600">
                    <AlertCircle className="w-4 h-4" />
                    <span>Wird zum {formatDate(subscription.premium_end_date || subscription.current_period_end)} gekündigt</span>
                  </div>
                )}
              </div>

              {isTrialAvailable && (
                <Button 
                  onClick={handleStartTrial}
                  disabled={isLoading}
                  className="w-full bg-gradient-to-r from-purple-400 to-purple-600 hover:from-purple-500 hover:to-purple-700 mb-2"
                >
                  <Gift className="w-4 h-4 mr-2" />
                  30 Tage kostenlos testen
                </Button>
              )}

              {(subscription?.tier === 'free' || activeProductCount >= getCurrentPlan().maxActiveProducts) && (
                <Button 
                  className="w-full bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700" 
                  onClick={showUpgradeModal}
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  {subscription?.tier === 'free' ? 'Jetzt upgraden' : 'Mehr Produkte benötigt? Upgrade!'}
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>

      {/* Konto-Lösch-Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex justify-between items-center">
              <AlertDialogTitle className="text-red-600 flex items-center gap-2">
                <Trash className="h-5 w-5" />
                Konto löschen
              </AlertDialogTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0 rounded-full hover:bg-red-50 text-red-500" 
                onClick={() => setShowDeleteDialog(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <AlertDialogDescription className="space-y-4">
              <div>
                Bist du sicher, dass du dein Konto und alle zugehörigen Daten unwiderruflich löschen möchtest? 
                Diese Aktion kann nicht rückgängig gemacht werden.
              </div>
              
              <div className="bg-red-50 border border-red-200 rounded-md p-3 my-2">
                <div className="text-red-800 text-sm font-medium mb-2">Wichtige Hinweise:</div>
                <ul className="text-red-700 text-sm space-y-1">
                  <li>• Alle deine Wartelisten-Produkte werden gelöscht</li>
                  <li>• Deine Statistiken und Fortschritte gehen verloren</li>
                  <li>• Es erfolgt keine Rückerstattung für gekaufte Premium-Lizenzen</li>
                  <li>• Du verlierst sofort den Zugriff auf alle Premium-Funktionen</li>
                </ul>
              </div>
              
              <div className="pt-2">
                <Label htmlFor="confirm" className="text-sm text-gray-700 font-semibold">
                  Bitte gib "LÖSCHEN" ein, um zu bestätigen:
                </Label>
                <Input
                  id="confirm"
                  className="mt-1 border-red-300 focus:border-red-500 focus:ring-red-500"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="LÖSCHEN"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoading}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDeleteAccount();
              }}
              disabled={isLoading || deleteConfirmText !== "LÖSCHEN"}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-500"
            >
              {isLoading ? 'Wird gelöscht...' : 'Konto endgültig löschen'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
} 